import clsx from "clsx";

interface SingleSelectOption {
	value: string;
}

interface SingleSelectProps {
	label?: string;
	options: SingleSelectOption[];
	selectedValue: string;
	onChange: (value: string) => void;
	width?: "fit" | string;
	height?: string;
	styleIndex?: number;
	customClassName?: string;
	firstClass?: boolean;
	wrap?: boolean;
	gap?: string;
	activeColor?: string;
	activeBg?: string;
	variant?: "primary" | "blue";
	flexCol?: boolean;
	customBorderColor?: string;
	display?: string;
	parentClassName?: string;
	uppercaseText?: boolean;
	filterPanel?: boolean;
}

const SingleSelectGroup = ({
	label,
	options,
	selectedValue,
	onChange,
	firstClass = false,
	width = "40px",
	height = "40px",
	styleIndex,
	customClassName,
	wrap,
	gap = "16px",
	activeColor = "#456930",
	activeBg = "#456930",
	variant = "primary",
	flexCol = false,
	customBorderColor,
	display = "flex",
	parentClassName,
	uppercaseText = false,
	filterPanel = false,
}: SingleSelectProps) => {
	return (
		<div className="mb-4 flex flex-col space-y-4">
			{label && (
				<span className="font-medium text-z-shark text-lg">{label}</span>
			)}
			<div
				style={{ gap: gap, display: display }}
				className={clsx(
					"items-center overflow-x-auto no-scrollbar",
					wrap && "flex-wrap",
					flexCol && "flex-col",
					filterPanel ? "overflow-hidden" : "overflow-x-auto no-scrollbar",
					parentClassName,
				)}
			>
				{options.map((option, index) => {
					const isSelected = selectedValue === option.value;
					const backgroundColor = isSelected ? activeBg : "white";
					const textColor =
						variant === "primary"
							? isSelected
								? "white"
								: activeColor
							: isSelected
								? activeColor
								: "#82B8FF";
					const borderColor = customBorderColor
						? isSelected
							? customBorderColor
							: "rgba(0,0,0,0.03)"
						: isSelected
							? "transparent"
							: activeBg;

					return (
						// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							key={index}
							style={{
								width,
								height,
								minWidth: width,
								backgroundColor,
								color: textColor,
								border: `2px solid ${borderColor}`,
							}}
							className={clsx(
								firstClass && index === 0 && "!w-[85px] !min-w-[85px]",
								"border flex items-center justify-center rounded-full",
								width === "fit" && "w-fit",
								styleIndex && "w-[90px]",
								customClassName,
							)}
							onClick={() => onChange(option.value)}
						>
							<span className="text-base font-bold">
								{uppercaseText ? option.value.toUpperCase() : option.value}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};

const PropertyTypeSelectGroup = ({
	label,
	options,
	selectedValue,
	onChange,
	firstClass = false,
	width = "40px",
	height = "40px",
	styleIndex,
	customClassName,
	wrap,
	gap = "16px",
	activeColor = "#456930",
	activeBg = "#456930",
	variant = "primary",
	flexCol = false,
	customBorderColor,
	display = "flex",
	parentClassName,
	uppercaseText = false,
	filterPanel = false,
}: any) => {
	return (
		<div className="mb-4 flex flex-col space-y-4">
			{label && (
				<span className="font-medium text-z-shark text-lg">{label}</span>
			)}
			<div
				style={{ gap: gap, display: display }}
				className={clsx(
					"items-center overflow-x-auto no-scrollbar",
					wrap && "flex-wrap",
					flexCol && "flex-col",
					filterPanel ? "overflow-hidden" : "overflow-x-auto no-scrollbar",
					parentClassName,
				)}
			>
				{options.map((option: any, index: any) => {
					let isSelected = false;

					if (selectedValue === null && index === 0) {
						isSelected = true;
					} else if (typeof selectedValue === "boolean") {
						isSelected = selectedValue === option.value;
					} else if (selectedValue?.includes(option.value)) {
						isSelected = true;
					}

					const backgroundColor = isSelected ? activeBg : "white";
					const textColor =
						variant === "primary"
							? isSelected
								? "white"
								: activeColor
							: isSelected
								? activeColor
								: "#82B8FF";

					return (
						<div
							key={index}
							style={{
								width,
								height,
								minWidth: width,
								backgroundColor,
								color: textColor,
							}}
							className={clsx(
								firstClass && index === 0 && "!w-[85px] !min-w-[85px]",
								"border flex items-center justify-center rounded-full",
								width === "fit" && "w-fit",
								styleIndex && "w-[90px]",
								customClassName,
							)}
							onClick={() => {
								onChange(option.value);
							}}
							onKeyDown={() => {
								onChange(option.value);
							}}
						>
							<span className="text-base font-bold">
								{uppercaseText ? option.label.toUpperCase() : option.label}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export { SingleSelectGroup, PropertyTypeSelectGroup };
